import dayjs from "dayjs";
import { z } from "zod";

import {
  dateString,
  optionalEmail,
  optionalPhone,
  optionalText,
  requiredText
} from "@/lib/forms/schema";

import type {
  CreateForeignNationalDependentRequest,
  DependentRelationship,
  ForeignNationalDependent
} from "../../../../api/types/foreignNationalDependent";
import { RELATIONSHIP_ORDER, sortDependents } from "../../lib/dependents";
import {
  dateInputToIso,
  normalizeText,
  toDateInputValue
} from "../../lib/format";

export type RelationshipFilter = "ALL" | DependentRelationship;

export type DependentDraft = {
  relationship: DependentRelationship | "";
  firstName: string;
  middleName: string;
  lastName: string;
  birthday: string;
  email: string;
  phone: string;
  notes: string;
  countryOfBirth: string;
  countryOfCitizenship: string;
  currentImmigrationStatus: string;
  immigrationStatusExpiration: string;
  priorityDate: string;
  preferenceCategory: string;
  eadExpiration: string;
  advanceParoleExpiration: string;
  passportExpiration: string;
  visaExpiration: string;
  /** Preserved, not edited here. */
  linkedForeignNationalId: string;
};

export type DependentTextField = Exclude<keyof DependentDraft, "relationship">;

export type DependentPayload = CreateForeignNationalDependentRequest;

export const emptyDependentDraft = (): DependentDraft => ({
  relationship: "",
  firstName: "",
  middleName: "",
  lastName: "",
  birthday: "",
  email: "",
  phone: "",
  notes: "",
  countryOfBirth: "",
  countryOfCitizenship: "",
  currentImmigrationStatus: "",
  immigrationStatusExpiration: "",
  priorityDate: "",
  preferenceCategory: "",
  eadExpiration: "",
  advanceParoleExpiration: "",
  passportExpiration: "",
  visaExpiration: "",
  linkedForeignNationalId: ""
});

export const dependentToDraft = (
  d: ForeignNationalDependent
): DependentDraft => ({
  relationship: d.relationship,
  firstName: normalizeText(d.first_name),
  middleName: normalizeText(d.middle_name),
  lastName: normalizeText(d.last_name),
  birthday: toDateInputValue(d.birthday),
  email: normalizeText(d.email),
  phone: normalizeText(d.phone),
  notes: normalizeText(d.notes),
  countryOfBirth: normalizeText(d.country_of_birth),
  countryOfCitizenship: normalizeText(d.country_of_citizenship),
  currentImmigrationStatus: normalizeText(d.current_immigration_status),
  immigrationStatusExpiration: toDateInputValue(
    d.immigration_status_expiration
  ),
  priorityDate: toDateInputValue(d.priority_date),
  preferenceCategory: normalizeText(d.preference_category),
  eadExpiration: toDateInputValue(d.ead_expiration),
  advanceParoleExpiration: toDateInputValue(d.advance_parole_expiration),
  passportExpiration: toDateInputValue(d.passport_expiration),
  visaExpiration: toDateInputValue(d.visa_expiration),
  linkedForeignNationalId: normalizeText(d.linked_foreign_national_id)
});

export const dependentDraftsEqual = (
  a: DependentDraft,
  b: DependentDraft
): boolean =>
  (Object.keys(a) as Array<keyof DependentDraft>).every(
    (k) => normalizeText(a[k]) === normalizeText(b[k])
  );

/** Relationship and a first / last name; everything else is optional. */
export const dependentDraftSchema: z.ZodType<DependentDraft, DependentDraft> =
  z.object({
    relationship: z
      .string()
      .refine((v) => v.length > 0, "Relationship is required.") as z.ZodType<
      DependentDraft["relationship"],
      DependentDraft["relationship"]
    >,
    firstName: requiredText("First name"),
    middleName: optionalText(),
    lastName: requiredText("Last name"),
    birthday: dateString(),
    email: optionalEmail(),
    phone: optionalPhone(),
    notes: optionalText(),
    countryOfBirth: optionalText(),
    countryOfCitizenship: optionalText(),
    currentImmigrationStatus: optionalText(),
    immigrationStatusExpiration: dateString(),
    priorityDate: dateString(),
    preferenceCategory: optionalText(),
    eadExpiration: dateString(),
    advanceParoleExpiration: dateString(),
    passportExpiration: dateString(),
    visaExpiration: dateString(),
    linkedForeignNationalId: optionalText()
  });

const orNull = (s: string): string | null => normalizeText(s) || null;

export const dependentDraftToPayload = (
  d: DependentDraft
): DependentPayload => ({
  relationship: (d.relationship || "OTHER") as DependentRelationship,
  first_name: normalizeText(d.firstName),
  middle_name: orNull(d.middleName),
  last_name: normalizeText(d.lastName),
  birthday: dateInputToIso(d.birthday),
  email: orNull(d.email),
  phone: orNull(d.phone),
  notes: orNull(d.notes),
  country_of_birth: orNull(d.countryOfBirth),
  country_of_citizenship: orNull(d.countryOfCitizenship),
  current_immigration_status: orNull(d.currentImmigrationStatus),
  immigration_status_expiration: dateInputToIso(d.immigrationStatusExpiration),
  priority_date: dateInputToIso(d.priorityDate),
  preference_category: orNull(d.preferenceCategory),
  ead_expiration: dateInputToIso(d.eadExpiration),
  advance_parole_expiration: dateInputToIso(d.advanceParoleExpiration),
  passport_expiration: dateInputToIso(d.passportExpiration),
  visa_expiration: dateInputToIso(d.visaExpiration),
  linked_foreign_national_id: orNull(d.linkedForeignNationalId)
});

/** `sortDependents` as a comparator for `useRecordCrud`. */
export const compareDependents = (
  a: ForeignNationalDependent,
  b: ForeignNationalDependent
): number => {
  const sorted = sortDependents([a, b]);
  return sorted[0] === a ? -1 : 1;
};

/** Relationships present in the list, in canonical order (for the filter chips). */
export const relationshipFilters = (
  list: ForeignNationalDependent[]
): Array<{ value: DependentRelationship; count: number }> => {
  const counts = new Map<DependentRelationship, number>();
  list.forEach((d) =>
    counts.set(d.relationship, (counts.get(d.relationship) ?? 0) + 1)
  );
  return RELATIONSHIP_ORDER.filter((r) => counts.has(r)).map((r) => ({
    value: r,
    count: counts.get(r) ?? 0
  }));
};

export type VisaStatusTone = "none" | "expired" | "pending" | "active";

export const visaStatusTone = (d: ForeignNationalDependent): VisaStatusTone => {
  const label = normalizeText(d.current_immigration_status);
  if (!label) return "none";
  const exp = normalizeText(d.immigration_status_expiration);
  const expDate = exp ? dayjs(exp) : null;
  if (expDate && expDate.isValid() && expDate.isBefore(dayjs(), "day"))
    return "expired";
  if (label.toLowerCase().includes("pending")) return "pending";
  return "active";
};

export const dependentFocusDomId = (id: string): string => `dependent-${id}`;
